import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { SITE, canonicalFor } from '../lib/site';
import { ROUTE_SEO } from '../data/routeSeo';
import { getLocalBusinessSchema } from '../data/reviewSources';

const DEFAULT_IMAGE = `${SITE}/petsmart-logo.png`;

/**
 * Title, description, canonical, Open Graph and Twitter tags for a page.
 * Values come from props, falling back to ROUTE_SEO for the current path.
 * Rendered by react-helmet, and written into each route's static HTML at
 * build time by scripts/prerender.js.
 */
const SEO = ({ title, description, path, image, type = 'website', noindex = false, breadcrumbs = true, children }) => {
  const { pathname } = useLocation();
  const route = path || pathname;
  const preset = ROUTE_SEO[route.replace(/\/+$/, '') || '/'] || {};
  const t = title || preset.title;
  const d = description || preset.description;
  const url = canonicalFor(route);
  const img = image ? (image.startsWith('http') ? image : `${SITE}${image}`) : DEFAULT_IMAGE;

  // Same business + website entities on every page (stable @ids), plus a
  // breadcrumb for inner pages. Blog posts pass breadcrumbs={false} and add
  // their own Home > Blog > Post trail.
  const { '@context': _ctx, ...store } = getLocalBusinessSchema();
  const graph = [
    { '@type': 'WebSite', '@id': `${SITE}/#website`, url: `${SITE}/`, name: 'Petsmart Puppies', inLanguage: 'en-IN', publisher: { '@id': store['@id'] } },
    store,
  ];
  const clean = route.replace(/\/+$/, '');
  if (breadcrumbs && clean && !noindex) {
    graph.push({
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: t ? t.split(' | ')[0] : clean.slice(1), item: url },
      ],
    });
  }

  return (
    <Helmet>
      {t && <title>{t}</title>}
      {d && <meta name="description" content={d} />}
      {noindex ? <meta name="robots" content="noindex, follow" /> : <link rel="canonical" href={url} />}
      <meta property="og:site_name" content="Petsmart Puppies" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:type" content={type} />
      {t && <meta property="og:title" content={t} />}
      {d && <meta property="og:description" content={d} />}
      {!noindex && <meta property="og:url" content={url} />}
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      {t && <meta name="twitter:title" content={t} />}
      {d && <meta name="twitter:description" content={d} />}
      <meta name="twitter:image" content={img} />
      <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })}</script>
      {children}
    </Helmet>
  );
};

export default SEO;
